import React, { useMemo } from "react";
import { Play, ArrowDown } from "lucide-react";
import VHSTape from "./VHSTape";
import { profile, categories } from "../mock/mock";

const Hero = ({ tapes, onSelectTape, activeCategory, setActiveCategory, filtered }) => {
  const shelfTapes = useMemo(() => filtered, [filtered]);

  return (
    <section
      id="reel"
      className="relative pt-28 md:pt-36 pb-6 grain scanlines overflow-hidden"
      style={{
        background:
          "radial-gradient(1200px 600px at 50% -10%, #1a120c 0%, #0e0b09 60%, #0e0b09 100%)",
      }}
    >
      {/* Corner meta */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 flex justify-between text-[11px] mono text-[#f2ece1]/45">
        <div>SP • 240min • HiFi Stereo</div>
        <div className="hidden md:block">TRK — 01 / 08 &nbsp; TC 00:00:12:04</div>
        <div>{profile.location}</div>
      </div>

      {/* Hero title */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-6 md:pt-8">
        <div className="flex items-baseline justify-between gap-6">
          <div className="text-[11px] md:text-xs mono text-[#f5b14e] tracking-[0.3em] uppercase">
            ▶ Now Playing — The Evan Lowenberg Tapes
          </div>
          <div className="hidden md:block text-[11px] mono text-[#f2ece1]/45">
            {profile.yearsActive}
          </div>
        </div>

        <h1 className="hero-title mt-4 md:mt-6 text-[22vw] md:text-[16vw] leading-[0.82]">
          <span className="block">{profile.brand}</span>
        </h1>

        <div className="mt-4 md:mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <p className="max-w-xl text-[15px] md:text-base text-[#f2ece1]/70 leading-relaxed">
            {profile.tagline}. A hand-picked shelf of films, spots, and
            experiments — pull a tape, hit play, roll the reel.
          </p>
          <div className="flex items-center gap-4 text-[11px] mono text-[#f2ece1]/50">
            <a
              href="#archive"
              className="flex items-center gap-2 hover:text-[#f5b14e] transition-colors"
            >
              <ArrowDown className="w-3.5 h-3.5" /> Scroll to the archive
            </a>
          </div>
        </div>
      </div>

      {/* Shelf */}
      <div className="relative z-10 mt-10 md:mt-14 shelf-perspective">
        <div className="tape-track flex items-end justify-center gap-3 md:gap-4 px-6 md:px-10 min-h-[360px] md:min-h-[440px]">
          {shelfTapes.map((t, i) => (
            <VHSTape
              key={t.id}
              tape={t}
              index={i}
              total={shelfTapes.length}
              onClick={() => onSelectTape(t)}
            />
          ))}
        </div>

        {/* Wooden shelf line */}
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
          <div
            className="h-[10px] rounded-[2px]"
            style={{
              background:
                "linear-gradient(180deg, #3a2a1f 0%, #1c130d 40%, #0e0b09 100%)",
              boxShadow:
                "0 20px 60px -20px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.06)",
            }}
          />
          <div className="h-4 bg-gradient-to-b from-black/70 to-transparent" />
        </div>
      </div>

      {/* Category chips */}
      <div className="relative z-10 mt-6 md:mt-8 px-6 md:px-10 max-w-[1400px] mx-auto">
        <div className="flex flex-wrap items-center gap-2 md:gap-3 justify-center">
          {["All", ...categories].map((c) => {
            const active = c === activeCategory;
            return (
              <button
                key={c}
                onClick={() => setActiveCategory(c)}
                className={`text-[12px] md:text-[13px] px-4 py-2 rounded-full border transition-all duration-300 ${
                  active
                    ? "bg-[#f2ece1] text-[#0e0b09] border-[#f2ece1]"
                    : "bg-transparent text-[#f2ece1]/75 border-[#2a221b] hover:border-[#f5b14e] hover:text-[#f5b14e]"
                }`}
              >
                {c}
              </button>
            );
          })}
        </div>
      </div>

      {/* Play instruction */}
      <div className="relative z-10 mt-8 flex justify-center">
        <div className="flex items-center gap-2 text-[11px] mono text-[#f2ece1]/45">
          <Play className="w-3.5 h-3.5" /> Hover a tape · click to play the reel
        </div>
      </div>
    </section>
  );
};

export default Hero;
